import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Target, AlertCircle } from "lucide-react"
import { useState, useEffect } from "react"
import { getValidFilterSections } from "@/utils/schema/schema-parser"
import type { ConfigComponentProps } from '../ConfigForm'

export function FilterConfig({ config, onUpdate, isLoading }: ConfigComponentProps): JSX.Element {
  const [filterScoreSection, setFilterScoreSection] = useState(
    config?.filter_score_section || ''
  )
  const [filterScoreField, setFilterScoreField] = useState(
    config?.filter_score_field || ''
  )

  const validFilterSections = getValidFilterSections(config?.prompt_config?.monitor?.json_schema)

  // Update parent when local state changes
  useEffect(() => {
    onUpdate({
      filter_score_section: filterScoreSection,
      filter_score_field: filterScoreField
    })
  }, [filterScoreSection, filterScoreField, onUpdate])

  // Update local state when config changes (workspace switch)
  useEffect(() => {
    setFilterScoreSection(config?.filter_score_section || '')
    setFilterScoreField(config?.filter_score_field || '')
  }, [config?.filter_score_section, config?.filter_score_field])

  const handleSectionChange = (section: string) => {
    setFilterScoreSection(section)
    setFilterScoreField('') // Reset field when section changes
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5 text-orange-500" />
          Filter Score Configuration
        </CardTitle>
        <CardDescription>
          Configure which section and field to use for filtering and scoring bills. Only sections with a single score field are available.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {validFilterSections.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No valid filter sections found</p>
            <p className="text-sm">Configure your schema first, then sections with single score fields will appear here</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Filter Score Section</Label>
              <Select
                value={filterScoreSection}
                onValueChange={handleSectionChange}
                disabled={isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a section" />
                </SelectTrigger>
                <SelectContent>
                  {validFilterSections.map(section => (
                    <SelectItem key={section.id} value={section.id}>
                      {section.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Filter Score Field</Label>
              <Select
                value={filterScoreField}
                onValueChange={setFilterScoreField}
                disabled={isLoading || !filterScoreSection}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a field" />
                </SelectTrigger>
                <SelectContent>
                  {validFilterSections
                    .find(s => s.id === filterScoreSection)
                    ?.fields.map(field => (
                      <SelectItem key={field.id} value={field.id}>
                        {field.title}
                      </SelectItem>
                    )) || []}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        
        {filterScoreSection && filterScoreField && (
          <div className="mt-4 p-3 bg-green-50 rounded-lg">
            <h5 className="text-sm font-medium mb-2 text-green-800">Current Configuration:</h5>
            <div className="text-xs text-green-700 font-mono space-y-1">
              <div>Section: {filterScoreSection}</div>
              <div>Field: {filterScoreField}</div>
              <div>Full Path: {filterScoreSection}.{filterScoreField}.score</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}